import { useTranslation } from 'react-i18next';

import { InfoCard } from 'core/components';
import { Email, PhoneNumber } from 'core/elements';
import { AlternateVersionHelper } from 'core/helpers';
import { getValue } from 'core/utils';
import { UpdateQuotedByLocation } from 'quotes/components/CustomerQuoteDetails/Tabs/CustomerQuoteBaseInformation';
import { QuoteEntityType } from 'quotes/enums';
import { ICustomerQuote } from 'quotes/types';

interface IProps {
  quote: ICustomerQuote;
  updateQuote: (updatedQuote?: ICustomerQuote) => void;
  entityType?: QuoteEntityType;
  isEditable?: boolean;
  isEditOpen: boolean;
  setIsEditOpen: (isOpen: boolean) => void;
}

export const QuotedByInfo = ({
  quote,
  updateQuote,
  entityType = QuoteEntityType.QUOTE,
  isEditable = true,
  isEditOpen,
  setIsEditOpen,
}: IProps) => {
  const { t } = useTranslation();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const alternateItems = isAlternateVersionEnabled
    ? [
        {
          label: t('labelIsrEmployee'),
          value:
            quote.isr?.employeeId || quote.isr?.default
              ? [
                  getValue(quote, 'isr.employeeName'),
                  <Email key="isrEmail" email={quote.isr?.email as string} />,
                ]
              : null,
          required: QuoteEntityType.ORDER === entityType,
          requiredMessage:
            QuoteEntityType.ORDER === entityType
              ? t('messageRequiredForOrderSubmission')
              : undefined,
        },
        {
          label: t('labelOsrEmployee'),
          value:
            quote.osr?.employeeId || quote.osr?.default
              ? [
                  getValue(quote, 'osr.employeeName'),
                  <Email key="osrEmail" email={quote.osr?.email as string} />,
                ]
              : null,
          required: QuoteEntityType.ORDER === entityType,
          requiredMessage:
            QuoteEntityType.ORDER === entityType
              ? t('messageRequiredForOrderSubmission')
              : undefined,
        },
      ]
    : [];

  const handleOnClose = (updatedQuote?: ICustomerQuote) => {
    setIsEditOpen(false);
    updateQuote(updatedQuote);
  };

  return (
    <>
      <InfoCard
        title={t('titleQuotedBy')}
        items={[
          {
            label: getValue(quote, 'ownerCompanyName') as string,
            value: [
              getValue(quote, 'ownerUserName'),
              <PhoneNumber
                key="ownerUserPhone"
                phone={getValue(quote, 'ownerUserPhone') as string}
              />,
              getValue(quote, 'locationName'),
              getValue(
                quote,
                isAlternateVersionEnabled
                  ? 'location.externalLocationId'
                  : 'location.vendorIdentifier'
              ),
            ],
          },
          ...alternateItems,
        ]}
        onEdit={() => setIsEditOpen(true)}
        isEditable={isEditable}
      />

      <UpdateQuotedByLocation
        isOpen={isEditOpen}
        quote={quote}
        onClose={handleOnClose}
        entityType={entityType}
      />
    </>
  );
};
